import { useEffect, useRef, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { AntiMetalButton } from '../components/ui/anti-metal-button';
import './GrowthServices.css'; /* reuse same card styles */

const PILLARS = [
  {
    id: 'automation',
    num: '01',
    label: 'AI Automation',
    headline: 'Reduce repetitive work and create smarter workflows.',
    body: 'We map the processes your team repeats every day and build automation that handles them — so your people can focus on the work that actually needs them.',
    tags: [
      'Workflow Automation',
      'Process Mapping',
      'Trigger-Based Systems',
      'AI-Powered Routing',
      'Automated Reporting',
      'Notification Systems',
    ],
    accent: '#a855f7',
  },
  {
    id: 'agents',
    num: '02',
    label: 'AI Agents',
    headline: 'Build agents designed around specific tasks, processes and business functions.',
    body: 'Not general-purpose chatbots. Agents built for a specific job — customer support, lead qualification, internal knowledge retrieval, campaign monitoring.',
    tags: [
      'Customer Support Agents',
      'Lead Qualification',
      'Internal Knowledge Bots',
      'Campaign Monitoring',
      'Agentic Workflows',
      'Multi-Step Reasoning',
    ],
    accent: '#7c3aed',
  },
  {
    id: 'custom',
    num: '03',
    label: 'Custom AI Solutions',
    headline: "When off-the-shelf tools aren't enough.",
    body: "Generic AI tools are built for the average use case. We help you build around your specific problem — the one that doesn't fit neatly into any existing product.",
    tags: [
      'Custom Model Integration',
      'Fine-Tuning',
      'Proprietary Data Systems',
      'Bespoke AI Tooling',
      'API Development',
      'AI Product Development',
    ],
    accent: '#c084fc',
  },
  {
    id: 'integration',
    num: '04',
    label: 'AI Integration & Strategy',
    headline: 'Connect AI with the systems your business already uses.',
    body: 'Identify the right opportunities before investing in the technology. We map where AI creates real leverage in your business — then connect it to the tools already in place.',
    tags: [
      'AI Integration',
      'System Connectivity',
      'AI Strategy',
      'Opportunity Mapping',
      'ROI Assessment',
      'Implementation Roadmap',
    ],
    accent: '#e879f9',
  },
];

function PillarCard({ pillar, index }) {
  const cardRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className={`gs-card${visible ? ' gs-card--visible' : ''}`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <div className="gs-card-top">
        <span className="gs-card-num" style={{ color: pillar.accent }}>{pillar.num}</span>
        <span className="gs-card-label" style={{ color: pillar.accent, borderColor: `${pillar.accent}30`, background: `${pillar.accent}10` }}>
          {pillar.label}
        </span>
      </div>
      <div className="gs-card-rule" style={{ background: `linear-gradient(90deg, ${pillar.accent}, transparent)` }} />
      <h3 className="gs-card-headline">{pillar.headline}</h3>
      <p className="gs-card-body">{pillar.body}</p>
      <div className="gs-tags">
        {pillar.tags.map((tag) => (
          <span key={tag} className="gs-tag" style={{ borderColor: `${pillar.accent}25`, color: 'rgba(255,255,255,0.65)' }}>
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function AurelixAIServices() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('gs-section--visible'); },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="gs-section" id="ai-services" ref={sectionRef}>
      <div className="gs-header">
        <p className="gs-eyebrow" style={{ color: '#a855f7' }}>What We Do</p>
        <h2 className="gs-heading">
          AI that works around<br />
          <span style={{
            background: 'linear-gradient(120deg, #fff 10%, rgba(168,85,247,0.9) 60%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            your actual business.
          </span>
        </h2>
        <p className="gs-subhead">
          Not a generic implementation. Every solution is designed around the specific friction, workflow or opportunity we identified together.
        </p>
      </div>

      <div className="gs-grid">
        {PILLARS.map((pillar, i) => (
          <PillarCard key={pillar.id} pillar={pillar} index={i} />
        ))}
      </div>

      <div className="gs-cta">
        <AntiMetalButton
          as="a"
          href="https://calendly.com/aurelixlabs/30min"
          target="_blank"
          rel="noopener noreferrer"
          accentFrom="#a855f7"
          accentTo="#7c3aed"
        >
          Explore Aurelix AI
          <ArrowUpRight size={16} style={{ marginLeft: 4 }} />
        </AntiMetalButton>
      </div>
    </section>
  );
}
